// --- Our Modules --------------------------------------------------------------------------------
import type { TyBranch } from "@/types";
import useTranslation from "@/hooks/useTranslation";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

type ConfiguredProps = {
    variant: "configured";
    branch: TyBranch;
    index: number;
    // CRUD context (SubscriptionBranchesScreen)
    onMore?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    // Branch-menu context (BranchesScreen)
    onPreview?: () => void;
    onReset?: () => void;
};

type UnconfiguredProps = {
    variant: "unconfigured";
    index: number;
    // CRUD context
    onAdd?: () => void;
    // Branch-menu context
    onConfigure?: () => void;
};

type Props = ConfiguredProps | UnconfiguredProps;

// --- Helpers ------------------------------------------------------------------------------------

const AR_ORDINALS = ["الأول", "الثاني", "الثالث", "الرابع", "الخامس", "السادس", "السابع", "الثامن", "التاسع", "العاشر"];
const EN_ORDINALS = ["1st", "2nd", "3rd", "4th", "5th", "6th", "7th", "8th", "9th", "10th"];

// --- Logic --------------------------------------------------------------------------------------

export default function BranchCard(props: Props) {
    const t = useTranslation();
    const isAr = t.branches.slotName === "الفرع";

    const ordinals = isAr ? AR_ORDINALS : EN_ORDINALS;
    const ordinal = ordinals[props.index] ?? String(props.index + 1);
    const slotLabel = `${t.branches.slotName} ${ordinal}`;

    if (props.variant === "unconfigured") {
        return (
            <div className="branch-card branch-card--unconfigured">
                <p className="branch-card-slot-name">{slotLabel}</p>
                {props.onAdd && (
                    <button type="button" className="branch-card-add-btn" onClick={props.onAdd}>
                        {t.branches.addBranchData}
                    </button>
                )}
                {props.onConfigure && (
                    <button type="button" className="branch-card-configure-btn" onClick={props.onConfigure}>
                        {t.branchMenu.configureLabel}
                    </button>
                )}
                <p className="branch-card-unconfigured-note">{props.onConfigure ? t.branchMenu.configureNote : t.branches.unconfiguredNote}</p>
            </div>
        );
    }

    const { branch, onMore, onPreview, onReset } = props;
    const hasOverrides = branch.inActiveCount > 0 || branch.hiddenCount > 0 || branch.pricesCount > 0;

    // CRUD context: show header + address + bottom row with more button
    if (onMore) {
        return (
            <div className="branch-card branch-card--configured">
                <div className="branch-card-header">
                    <div className="branch-card-icon-box">
                        <img src="/icons/side-bar/branches-icon-dark.svg" alt="" />
                    </div>
                    <div className="branch-card-title-group">
                        <span className="branch-card-title-label">{t.branches.slotName}:</span>
                        <span className="branch-card-title-name">{branch.name}</span>
                    </div>
                    <div className="branch-card-slug-badge">
                        <span className="branch-card-slug-prefix">{t.branches.slugLabel}: </span>
                        <span className="branch-card-slug-value">{branch.slug}</span>
                    </div>
                </div>

                {branch.address && (
                    <div className="branch-card-address">
                        <span className="branch-card-address-label">{t.branches.addressLabel}:</span>
                        <span className="branch-card-address-value">{branch.address}</span>
                    </div>
                )}

                <div className="branch-card-bottom-row">
                    {!hasOverrides && <span className="branch-card-stat-badge">{t.branches.noOverridesLabel}</span>}
                    {hasOverrides && (
                        <>
                            {branch.inActiveCount > 0 && (
                                <span className="branch-card-stat-badge">
                                    {branch.inActiveCount} {t.branches.inActiveLabel}
                                </span>
                            )}
                            {branch.hiddenCount > 0 && (
                                <span className="branch-card-stat-badge">
                                    {branch.hiddenCount} {t.branches.hiddenLabel}
                                </span>
                            )}
                            {branch.pricesCount > 0 && (
                                <span className="branch-card-stat-badge">
                                    {branch.pricesCount} {t.branches.pricesLabel}
                                </span>
                            )}
                        </>
                    )}
                    <div className="branch-card-bottom-spacer" />
                    <button
                        type="button"
                        className="branch-card-more-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            onMore(e);
                        }}
                    >
                        <img src="/icons/more-icon.svg" alt="" width={16} height={16} />
                    </button>
                </div>
            </div>
        );
    }

    // Branch-menu context: no address, stats row + full-width actions row
    return (
        <div className="branch-card branch-card--configured branch-card--menu-context">
            <div className="branch-card-header">
                <div className="branch-card-icon-box">
                    <img src="/icons/side-bar/branches-icon-dark.svg" alt="" />
                </div>
                <div className="branch-card-title-group">
                    <span className="branch-card-title-label">{t.branches.slotName}:</span>
                    <span className="branch-card-title-name">{branch.name}</span>
                </div>
                <div className="branch-card-slug-badge">
                    <span className="branch-card-slug-prefix">{t.branches.slugLabel}: </span>
                    <span className="branch-card-slug-value">{branch.slug}</span>
                </div>
            </div>

            <div className="branch-card-stats-row">
                {!hasOverrides && <span className="branch-card-stat-badge">{t.branches.noOverridesLabel}</span>}
                {hasOverrides && (
                    <>
                        {branch.inActiveCount > 0 && (
                            <span className="branch-card-stat-badge">
                                {branch.inActiveCount} {t.branches.inActiveLabel}
                            </span>
                        )}
                        {branch.hiddenCount > 0 && (
                            <span className="branch-card-stat-badge">
                                {branch.hiddenCount} {t.branches.hiddenLabel}
                            </span>
                        )}
                        {branch.pricesCount > 0 && (
                            <span className="branch-card-stat-badge">
                                {branch.pricesCount} {t.branches.pricesLabel}
                            </span>
                        )}
                    </>
                )}
            </div>

            <div className="branch-card-actions-row">
                <button type="button" className="branch-card-preview-btn" onClick={onPreview}>
                    {t.branchMenu.previewMenuLabel}
                </button>
                {onReset && (
                    <button type="button" className="branch-card-reset-btn" onClick={onReset} disabled={!hasOverrides}>
                        {t.branchMenu.resetLabel}
                    </button>
                )}
            </div>
        </div>
    );
}
